/**
 * Pure helpers that translate a user's `notificationReadiness` +
 * `phone` into the per-channel affordances the send dialog needs:
 * which channels are selectable, and which will be silently skipped
 * because the recipient opted out.
 *
 * Kept side-effect-free so they're cheap to unit-test and reusable from
 * both the dialog and the readiness panel.
 */

import type { NotificationChannel, NotificationPreferences } from "./notification-types";

/**
 * SMS is only deliverable when the user has a phone number on file. The
 * backend rejects an SMS send with no phone (`400 SMS_NO_PHONE_ON_FILE`),
 * so the dialog disables the SMS option up front.
 */
export function isSmsAvailable(user: { readonly phone?: string }): boolean {
  return typeof user.phone === "string" && user.phone.trim().length > 0;
}

/** The opt-in flag on `preferences` that governs the given channel. */
export function channelPrefEnabled(
  prefs: NotificationPreferences,
  channel: NotificationChannel,
): boolean {
  switch (channel) {
    case "PUSH":
      return prefs.push;
    case "EMAIL":
      return prefs.email;
    case "SMS":
      return prefs.sms;
  }
}

/**
 * Whether a send on this channel will come back `SKIPPED_PREF` — i.e.
 * the recipient opted out and the admin has not ticked "ignore
 * preferences". Drives the contextual warning in the dialog.
 */
export function willSkipForPref(
  channel: NotificationChannel,
  prefs: NotificationPreferences,
  bypassPreferences: boolean,
): boolean {
  if (bypassPreferences) return false;
  return !channelPrefEnabled(prefs, channel);
}
